import React from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-200 flex flex-col">
      <Header />

      <main className="flex-1 pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-extrabold text-slate-900 dark:text-white mb-2">
            Terms of Service
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mb-12">
            Last updated February 07, 2026
          </p>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-12 text-slate-600 dark:text-slate-300">
            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                1. AGREEMENT TO OUR LEGAL TERMS
              </h2>
              <div className="space-y-4">
                <p>
                  We are Taskplan ("Company," "we," "us," or "our"). We operate
                  the website{" "}
                  <a
                    href="https://taskplan.run"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    taskplan.run
                  </a>{" "}
                  (the "Site"), as well as any other related products and
                  services that refer or link to these legal terms (the "Legal
                  Terms") (collectively, the "Services").
                </p>
                <p>
                  You can contact us by email at{" "}
                  <a
                    href="mailto:support@taskplan.run"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    support@taskplan.run
                  </a>
                  .
                </p>
                <p>
                  These Legal Terms constitute a legally binding agreement made
                  between you, whether personally or on behalf of an entity
                  ("you"), and Taskplan, concerning your access to and use of
                  the Services. You agree that by accessing the Services, you
                  have read, understood, and agreed to be bound by all of these
                  Legal Terms. IF YOU DO NOT AGREE WITH ALL OF THESE LEGAL
                  TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE
                  SERVICES AND YOU MUST DISCONTINUE USE IMMEDIATELY.
                </p>
                <p>
                  The Services are intended for users who are at least 18 years
                  old. Persons under the age of 18 are not permitted to use or
                  register for the Services.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                2. INTELLECTUAL PROPERTY RIGHTS
              </h2>
              <div className="space-y-4">
                <p>
                  We are the owner or the licensee of all intellectual property
                  rights in our Services, including all source code, databases,
                  functionality, software, website designs, audio, video, text,
                  photographs, and graphics in the Services (collectively, the
                  "Content"), as well as the trademarks, service marks, and
                  logos contained therein (the "Marks").
                </p>
                <p>
                  Our Content and Marks are protected by copyright and trademark
                  laws (and various other intellectual property rights and
                  unfair competition laws) and treaties in the United States and
                  around the world.
                </p>
                <p>
                  The Content and Marks are provided in or through the Services
                  "AS IS" for your personal, non-commercial use or internal
                  business purpose only.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                3. USER REPRESENTATIONS
              </h2>
              <div className="space-y-4">
                <p>By using the Services, you represent and warrant that:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    All registration information you submit will be true,
                    accurate, current, and complete.
                  </li>
                  <li>
                    You will maintain the accuracy of such information and
                    promptly update such registration information as necessary.
                  </li>
                  <li>
                    You have the legal capacity and you agree to comply with
                    these Legal Terms.
                  </li>
                  <li>
                    You are not a minor in the jurisdiction in which you reside.
                  </li>
                  <li>
                    You will not access the Services through automated or
                    non-human means, whether through a bot, script or otherwise.
                  </li>
                  <li>
                    You will not use the Services for any illegal or
                    unauthorised purpose.
                  </li>
                  <li>
                    Your use of the Services will not violate any applicable law
                    or regulation.
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                4. USER REGISTRATION
              </h2>
              <p>
                You may be required to register to use the Services. You agree
                to keep your password confidential and will be responsible for
                all use of your account and password. We reserve the right to
                remove, reclaim, or change a username you select if we
                determine, in our sole discretion, that such username is
                inappropriate, obscene, or otherwise objectionable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                5. PURCHASES AND PAYMENT
              </h2>
              <div className="space-y-4">
                <p>We accept the following forms of payment:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Visa</li>
                  <li>Mastercard</li>
                  <li>American Express</li>
                  <li>PayPal</li>
                  <li>Discover</li>
                </ul>
                <p>
                  You agree to provide current, complete, and accurate purchase
                  and account information for all purchases made via the
                  Services. You further agree to promptly update account and
                  payment information, including email address, payment method,
                  and payment card expiration date, so that we can complete your
                  transactions and contact you as needed.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                6. SUBSCRIPTIONS
              </h2>
              <div className="space-y-4">
                <p>
                  Your subscription will continue and automatically renew unless
                  cancelled. You consent to our charging your payment method on
                  a recurring basis without requiring your prior approval for
                  each recurring charge, until such time as you cancel the
                  applicable order.
                </p>
                <p>
                  You can cancel your subscription at any time by logging into
                  your account. Your cancellation will take effect at the end of
                  the current paid term. If you have any questions or are
                  unsatisfied with our Services, please email us at{" "}
                  <a
                    href="mailto:support@taskplan.run"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    support@taskplan.run
                  </a>
                  .
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                7. PROHIBITED ACTIVITIES
              </h2>
              <p>
                You may not access or use the Services for any purpose other
                than that for which we make the Services available. The Services
                may not be used in connection with any commercial endeavours
                except those that are specifically endorsed or approved by us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                8. USER DATA
              </h2>
              <p>
                We will maintain certain data that you transmit to the Services
                for the purpose of managing the performance of the Services, as
                well as data relating to your use of the Services. Although we
                perform regular routine backups of data, you are solely
                responsible for all data that you transmit or that relates to
                any activity you have undertaken using the Services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                9. DISCLAIMER
              </h2>
              <p className="uppercase">
                THE SERVICES ARE PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS.
                YOU AGREE THAT YOUR USE OF THE SERVICES WILL BE AT YOUR SOLE
                RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
                WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SERVICES
                AND YOUR USE THEREOF.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                10. LIMITATION OF LIABILITY
              </h2>
              <p className="uppercase">
                IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE
                LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT,
                CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE
                DAMAGES, INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR
                OTHER DAMAGES ARISING FROM YOUR USE OF THE SERVICES.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                11. INDEMNIFICATION
              </h2>
              <p>
                You agree to defend, indemnify, and hold us harmless, including
                our subsidiaries, affiliates, and all of our respective
                officers, agents, partners, and employees, from and against any
                loss, damage, liability, claim, or demand, including reasonable
                attorneys' fees and expenses, made by any third party due to or
                arising out of: (1) use of the Services; (2) breach of these
                Legal Terms; (3) any breach of your representations and
                warranties set forth in these Legal Terms; (4) your violation of
                the rights of a third party, including but not limited to
                intellectual property rights; or (5) any overt harmful act
                toward any other user of the Services with whom you connected
                via the Services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                12. MISCELLANEOUS
              </h2>
              <p>
                These Legal Terms and any policies or operating rules posted by
                us on the Services or in respect to the Services constitute the
                entire agreement and understanding between you and us. Our
                failure to exercise or enforce any right or provision of these
                Legal Terms shall not operate as a waiver of such right or
                provision. These Legal Terms operate to the fullest extent
                permissible by law. We may assign any or all of our rights and
                obligations to others at any time.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                13. CONTACT US
              </h2>
              <p>
                In order to resolve a complaint regarding the Services or to
                receive further information regarding use of the Services,
                please contact us at:
              </p>
              <div className="mt-4 p-6 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800">
                <p className="font-bold text-slate-900 dark:text-white">
                  Taskplan
                </p>
                <p>
                  Email:{" "}
                  <a
                    href="mailto:support@taskplan.run"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    support@taskplan.run
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
